import type { NumberFormat } from "@/lib/reports/schema";

export function unique<T>(arr: T[]): T[] {
  return Array.from(new Set(arr));
}

/**
 * Order category-axis values numerically when every value is numeric (e.g. hour
 * of day 0..23 returned unordered by Cube), otherwise preserve first-seen order
 * (e.g. day names, statuses). Keeps a heatmap/cohort axis readable.
 */
export function sortIfNumeric<T>(values: T[]): T[] {
  const allNumeric =
    values.length > 0 &&
    values.every((v) => v !== "" && v != null && !Number.isNaN(Number(v)));
  return allNumeric ? [...values].sort((a, b) => Number(a) - Number(b)) : values;
}

/**
 * Format a time-bucket axis label (e.g. "2026-04-01" → "Apr 26") based on
 * the granularity Cube returned. Falls back to the raw string when the
 * value isn't a recognisable ISO date.
 */
export function formatTimeBucket(raw: string, granularity?: string): string {
  if (!raw || raw === "undefined" || raw === "null") return "Unspecified";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const yy = String(d.getUTCFullYear()).slice(-2);
  switch (granularity) {
    case "year":
      return String(d.getUTCFullYear());
    case "quarter":
      return `Q${Math.floor(d.getUTCMonth() / 3) + 1} '${yy}`;
    case "month":
      return `${months[d.getUTCMonth()]} '${yy}`;
    case "week":
      return `${months[d.getUTCMonth()]} ${d.getUTCDate()}`;
    case "day":
      return `${months[d.getUTCMonth()]} ${d.getUTCDate()}`;
    case "hour": {
      // Hour-grain charts (e.g. 24-bucket "by hour of day") get
      // hour-precision labels — "Apr 15" alone would collapse every
      // bucket to the same string.
      const hh = String(d.getUTCHours()).padStart(2, "0");
      return `${months[d.getUTCMonth()]} ${d.getUTCDate()} ${hh}:00`;
    }
    default:
      return `${months[d.getUTCMonth()]} '${yy}`;
  }
}

/**
 * Collapse rows that share the same value in `categoryKey` into a single
 * row, aggregating each measure. Percent-formatted measures are averaged
 * (averaging rates across time periods is closer-to-right than summing);
 * everything else is summed.
 *
 * Use case: a bar chart with one dimension where the query also carries a
 * timeDimension. Without this we'd render one bar per (dim × time-bucket)
 * which is unreadable for "X by recruiter, ranked" prompts.
 *
 * When the caller is a stacked_bar (which needs the time-axis preserved for
 * each stack), `skipIfStacked` is passed in and we skip rollup. Stacks
 * are intentionally per-period.
 */
export function aggregateByCategory(
  rows: Array<Record<string, number | string | null>>,
  categoryKey: string,
  measures: string[],
  columns: Array<{ key: string; format?: NumberFormat }> | undefined,
  skipIfStacked: boolean,
): Array<Record<string, number | string | null>> {
  if (skipIfStacked) return rows;
  // Only worth rolling up if there are actual duplicates.
  const cats = rows.map((r) => String(r[categoryKey] ?? ""));
  if (cats.length === new Set(cats).size) return rows;

  const groups = new Map<
    string,
    { row: Record<string, number | string | null>; counts: Record<string, number> }
  >();
  const formats = new Map(measures.map((m) => [m, columns?.find((c) => c.key === m)?.format] as const));

  for (const r of rows) {
    const key = String(r[categoryKey] ?? "");
    let g = groups.get(key);
    if (!g) {
      g = { row: { [categoryKey]: r[categoryKey] }, counts: {} };
      groups.set(key, g);
    }
    for (const m of measures) {
      const v = Number(r[m] ?? 0);
      g.row[m] = (Number(g.row[m] ?? 0) || 0) + v;
      g.counts[m] = (g.counts[m] ?? 0) + 1;
    }
  }
  // Convert sums to averages for percent-formatted measures.
  for (const g of groups.values()) {
    for (const m of measures) {
      if (formats.get(m) === "percent") {
        const c = g.counts[m] || 1;
        g.row[m] = (Number(g.row[m]) || 0) / c;
      }
    }
  }
  return Array.from(groups.values()).map((g) => g.row);
}
